// Stage 5 C-A §7 — Client Programme (548-03/548-04).
// Renders only client-visible programme items — a projection over
// the canonical shared programme, not a portal-local copy.
import React, { useEffect, useState } from "react";
import { portalApi, fmtDate } from "./portalApi";
import { PageHeader, Card, CardHeader, Pill, EmptyState, Spinner } from "./PortalUi";
import { usePageMeta } from "../../components/Layout";

function stateTone(s) {
    if (!s) return "neutral";
    const x = String(s).toLowerCase();
    if (x === "achieved" || x === "complete" || x === "completed") return "ok";
    if (x === "in_progress") return "info";
    if (x === "at_risk" || x === "delayed") return "warn";
    return "neutral";
}

export default function PortalProgramme() {
    usePageMeta({ title: "Programme | Studio G4 Portal" });
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [mode, setMode] = useState("list");   // "gantt" | "list"
    const [wsFilter, setWsFilter] = useState("");
    const [stateFilter, setStateFilter] = useState("");
    const [expanded, setExpanded] = useState({});
    useEffect(() => {
        portalApi.programme()
            .then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, []);
    if (err) return <div className="py-8 px-4"><EmptyState heading="Programme unavailable" note={err} testid="portal-programme-err" /></div>;
    if (!data) return <Spinner testid="portal-programme-loading" />;

    const summary = data.programme_summary || {};
    const items = (data.items || []).filter(it => {
        if (wsFilter && String(it.linked_workstage) !== wsFilter) return false;
        if (stateFilter && it.state !== stateFilter) return false;
        return true;
    });
    const workstages = Array.from(new Set((data.items || []).map(i => i.linked_workstage).filter(Boolean))).sort();
    const states = Array.from(new Set((data.items || []).map(i => i.state).filter(Boolean)));

    return (
        <div className="py-8 px-4" data-testid="portal-programme">
            <PageHeader kicker="Timeline" title="Programme"
                right={
                    <div className="flex items-center gap-2" data-testid="portal-programme-mode">
                        <button onClick={() => setMode("gantt")} data-testid="portal-programme-mode-gantt"
                            className={`px-3 py-1 text-[12px] rounded font-manrope font-semibold ${mode === "gantt" ? "bg-charcoal text-white" : "bg-white border border-[#D7D4CC] text-[#4A4A46]"}`}>Gantt</button>
                        <button onClick={() => setMode("list")} data-testid="portal-programme-mode-list"
                            className={`px-3 py-1 text-[12px] rounded font-manrope font-semibold ${mode === "list" ? "bg-charcoal text-white" : "bg-white border border-[#D7D4CC] text-[#4A4A46]"}`}>List</button>
                    </div>
                }
                testid="portal-programme-header" />

            <Card className="mb-4" testid="portal-programme-summary">
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12.5px]">
                    <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Current workstage</div><div className="text-charcoal mt-1 font-manrope">{summary.current_workstage_label || "—"}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Next milestone</div><div className="text-charcoal mt-1 font-manrope">{summary.next_milestone || "—"}</div><div className="text-[11px] text-[#8A8A84]">{summary.next_milestone_at ? fmtDate(summary.next_milestone_at) : ""}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Status</div><div className="mt-1"><Pill tone={summary.status === "On track" ? "ok" : "warn"}>{summary.status || "—"}</Pill></div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Last updated</div><div className="text-charcoal mt-1 font-manrope">{fmtDate(summary.last_updated)}</div></div>
                </div>
            </Card>

            <Card className="mb-4"><div className="p-3 flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-[12px]">
                    <span className="text-[#8A8A84] uppercase tracking-[0.08em] text-[10.5px]">Workstage</span>
                    <select value={wsFilter} onChange={e => setWsFilter(e.target.value)} data-testid="portal-programme-filter-ws"
                        className="px-2 py-1 border border-[#D7D4CC] rounded bg-white">
                        <option value="">All</option>{workstages.map(w => <option key={w} value={w}>WS{w}</option>)}
                    </select>
                </label>
                <label className="flex items-center gap-2 text-[12px]">
                    <span className="text-[#8A8A84] uppercase tracking-[0.08em] text-[10.5px]">Status</span>
                    <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} data-testid="portal-programme-filter-state"
                        className="px-2 py-1 border border-[#D7D4CC] rounded bg-white">
                        <option value="">All</option>{states.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
            </div></Card>

            {!items.length ? (
                <EmptyState heading="No programme items match" note="Try clearing filters." testid="portal-programme-empty" />
            ) : mode === "gantt" ? (
                <Card testid="portal-programme-gantt">
                    <div className="p-4 overflow-x-auto">
                        <div className="min-w-[720px]">
                            {items.map(it => (
                                <div key={it.id} className="flex items-center gap-3 py-2 border-b border-[#EEEBE4]" data-testid={`portal-programme-gantt-${it.id}`}>
                                    <div className="w-56 shrink-0 text-[13px] text-charcoal font-manrope">{it.title}</div>
                                    <div className="flex-1 relative h-5 bg-[#F7F5EF] rounded">
                                        <div className="absolute top-0 left-[20%] right-[30%] h-full rounded bg-[#A8543F]/70"></div>
                                    </div>
                                    <div className="w-24 shrink-0 text-right text-[12px] text-[#6B6B67]">{fmtDate(it.target_at)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
            ) : (
                <Card testid="portal-programme-list">
                    <div className="divide-y divide-[#EEEBE4]">
                        {items.map(it => (
                            <div key={it.id} data-testid={`portal-programme-row-${it.id}`}>
                                <button className="w-full text-left px-5 py-3 flex items-center justify-between gap-3 hover:bg-[#FAF7F1]"
                                        onClick={() => setExpanded(prev => ({ ...prev, [it.id]: !prev[it.id] }))}
                                        data-testid={`portal-programme-toggle-${it.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-manrope text-[14px] text-charcoal">{it.title}</div>
                                        <div className="text-[11.5px] text-[#8A8A84] mt-0.5 uppercase tracking-[0.06em]">
                                            {it.kind}{it.workstage_label ? ` · ${it.workstage_label}` : ""}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[13px] text-charcoal font-manrope">{fmtDate(it.target_at)}</div>
                                        <div className="mt-1"><Pill tone={stateTone(it.state)}>{it.state}</Pill></div>
                                    </div>
                                </button>
                                {expanded[it.id] && (
                                    <div className="px-5 pb-4 pt-2 bg-[#FAF7F1]/50" data-testid={`portal-programme-detail-${it.id}`}>
                                        {it.description && <p className="text-[13px] text-[#4A4A46] leading-relaxed mb-2">{it.description}</p>}
                                        <div className="grid grid-cols-2 gap-3 text-[11.5px] text-[#6B6B67]">
                                            <div>Start: <span className="text-charcoal">{fmtDate(it.start_at)}</span></div>
                                            <div>Due: <span className="text-charcoal">{fmtDate(it.target_at)}</span></div>
                                        </div>
                                        {(it.linked_deliverables || []).length > 0 && (
                                            <div className="mt-2 text-[11.5px] text-[#6B6B67]">
                                                Linked deliverables: {it.linked_deliverables.map(d => d.filename).join(", ")}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}
